import PropTypes from "prop-types";
import { BOOKING_STEP, useSaveBookingStepUrl } from "app/features/booking/steps";
import { useContext, useEffect, useMemo, useRef } from "react";
import { differenceInDays, format } from "date-fns";
import last from "lodash/last";
import { logoutUser } from "app/pages/Auth/AuthActions";
import Quotation from "./Quotation";
import {
	abortAvailibilities,
	checkAvailibilities,
	fetchFlightOptions,
	preBook,
	saveQuotationSearchQueryString,
	showFlexRatesModal,
} from "app/pages/Booking/Quotation/quotationActionCreators";
import { updateBooking } from "app/pages/Booking/bookingActionCreators";
import { bindActionCreators } from "redux";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { bookingPropTypes, quotationPropTypes } from "app/utils/propTypes.js";
import {
	getIncludedFlight,
	getQuotationPriceChangeData,
	getTransfersOfSelectedFlight,
	getVisibleFlights,
} from "app/pages/Booking/Quotation/quotationSelectors.js";
import { clearPromotionValue } from "app/pages/Booking/PromotionCode/promotionCodeActionCreators";
import withRouter from "app/utils/hocs/withRouter";
import isEmpty from "lodash/isEmpty";
import {
	buildQuotationReloadUrl,
	extractQuotationPayloadFromQueryParams,
} from "app/utils/urlDataExtractor";
import { withAlert } from "app/pages/.shared/Alert/AlertContainer.jsx";
import { getStore } from "app/configureStore";
import {
	HTTP_STATUS_CODES,
	INSURANCE_STANDARD,
	OFFER_TYPES,
	QUOTATION_CODE_STATUS,
	QUOTATION_STATUS_VALIDE,
	SDP_ROTATION_DATE_FORMAT,
	SESSION_BIID_EXPIRATION_MAX_AGE,
	STRATEGY,
} from "app/constants";
import {
	calculatePreBookTotal,
	getFlightOptionsOfSelectedFlight,
	isBookingEligibleForDeposit,
} from "app/pages/Booking/bookingSelectors";
import get from "lodash/get";
import { useLoadingBar } from "app/features/loadingBar";
import { parseQueries } from "app/utils/routerUtils";
import { saveNavLog } from "app/pages/Listing/NavLogs/navLogActionCreators";
import { fetchFicheProduit } from "app/pages/Listing/productActionCreators";
import { checkIfNavlogEnabled } from "app/pages/Listing/NavLogs/navLogsSelector";
import { checkSpecialOfferOnQuote } from "app/pages/Booking/Payment/paymentSelector";
import { getSpecialOffer } from "app/pages/FicheProduit/ficheProduitSelector";
import {
	computeActivitiesDuplicateDates,
	handleFailPreBookResponse,
	handleSuccesPreBookResponse,
} from "app/utils/quotationUtils";
import {
	abortSDPAvailibilities,
	abortSdpQuote,
	checkSDPAvailibilities,
	clearSmartDP,
	fetchSdpQuote,
	saveSDPProduct,
	saveSDPSearchQueryString,
} from "app/pages/SmartDP/smartDPActionCreators";
import { getFallbackRedirectUrl, getSeoMetaData } from "app/reducers/partnerSelector";
import {
	hasOptionFlex,
	shouldEnableFlexRateChoice,
} from "app/pages/Booking/Quotation/flexSelectors";
import { getSessionBiid, getSessionBiidExpirationDate } from "app/pages/Auth/authSelectors";
import { hasEmptyParams, isRequestAborted } from "app/utils/utils.url";
import { isExpiredAfter } from "app/utils/utils";
import { useFsFlag } from "@flagship.io/react-sdk";
import AppGlobalsContext from "app/AppGlobalsContext";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import withFlagship from "app/utils/hocs/withFlagship";
import { getCredentials } from "app/utils/auth";
import { useLocation } from "react-router-dom";
import {
	abortStopoverAvailibilities,
	checkStopoverAvailibilities,
	fetchStopoverFlightOptions,
} from "app/pages/Stopover/stopoverActionCreators";
import { Formik } from "formik";
import { updateExchangeRate } from "app/reducers/exchangeRateActionCreators";
import HeadMetadata from "app/utils/metas/HeadMetadata";
import { useSessionTimeout } from "app/pages/Booking/Quotation/hooks/useSessionTimeout";
import { useIsFlagshipDecisionSettled } from "app/hooks";
import {
	buildBookingUpdatedData,
	buildQuotationPayload,
	hasFlightOptionsFailed,
	isSDPQuoteCodeReusable,
} from "app/pages/Booking/Quotation/utils/quotationUtils";
import {
	buildSDPQuotePayload,
	extractSDPSearchQueryFromQuoteQuery,
	hasSDPQuoteSearchParams,
	mapQueryParamsToBooking,
} from "app/utils/smartDPUtils";
import { stringify } from "qs";

const QuotationContainer = props => {
	useSaveBookingStepUrl(BOOKING_STEP.QUOTE);

	const stateProps = useSelector(mapStateToProps, shallowEqual);
	const dispatch = useDispatch();
	const dispatchProps = useMemo(() => mapDispatchToProps(dispatch), [dispatch]);

	const {
		sessionBiid,
		sessionBiidExpirationDate,
		formInitialValues,
		location = {},
		partnerCode,
		strategy,
		booking,
		ficheProduit,
		navigate,
		quotation,
		showAlert,
		isNavlogsEnabled,
		isBookingEligibleForDeposit,
		smartDPCode,
		sortedBy,
		selectedflightCode,
		fallbackRedirectUrl,
		token,
		abTestsValues,
		titleFromSanity,
		descriptionFromSanity,
		currency,
		sdpDestinations,
		sdpDepartureCities,
		sdpCabins,
		searchQueryString,
		sdpQuoteTimestamp,
	} = { ...props, ...stateProps };

	const {
		updateBooking,
		clearPromotionValue,
		fetchFicheProduit,
		showFlexRatesModal,
		checkAvailibilities = () => {},
		fetchFlightOptions = () => {},
		fetchStopoverFlightOptions = () => {},
		checkSDPAvailibilities,
		checkStopoverAvailibilities,
		logoutUser,
		saveQuotationSearchQueryString,
		preBook,
		updateExchangeRate,
		fetchSdpQuote,
		saveSDPProduct,
		saveSDPSearchQueryString,
	} = dispatchProps;
	const { show: showLoadingBar, finish: finishLoadingBar } = useLoadingBar();

	const ppFunnelFlags = useFsFlag("pp_funnel_flags");
	const ppFunnelFlagsValues = ppFunnelFlags.getValue(undefined);
	const isFlagshipDecisionSettled = useIsFlagshipDecisionSettled();
	const quotedSearchRef = useRef();
	const { brand, shop } = useContext(AppGlobalsContext);

	const { pathname } = useLocation();

	const isSDPQuote = pathname?.includes("/sdp/booking/quote");

	const isStopoverQuote = pathname?.includes("/stopover/booking/quote");

	const { track } = useAnalytics();

	// vrai quand la quote sdp a été rejouée depuis l'url, sans passer par le listing
	const isSDPDirectAccessRef = useRef(false);

	useSessionTimeout({
		isSDPQuote,
		isStopoverQuote,
		saveQuotationSearchQueryString,
		showAlert,
	});

	const trackQuotationResult = ({ status, data = {} }) => {
		// undefined = we don't know because we don't have endDate to compare (PP)
		// if endDate is available, hasArrivalDayShift will be true or false
		let hasArrivalDayShift = undefined;

		// endDate is defined on sdp, not for pp quote
		if (booking.endDate) {
			let endDateSearch = new Date(booking.endDate);
			endDateSearch = endDateSearch.setDate(endDateSearch.getDate() - 1);
			// because we want to compare to landingDate which is a string yyyy-MM-dd
			endDateSearch = format(new Date(endDateSearch), SDP_ROTATION_DATE_FORMAT);

			// il faut récupérer la flight included et non pas le premier flight dans la liste "flights"
			// car il y a des cas ou le flight qui est sélectionné par défaut n'est pas le premier
			const includedFlight = data.flights?.find(flight => flight.included === true);
			const lastLegOfIncludedFlight = last(includedFlight?.inbound?.legs);
			const landingDateIncludedFlight = lastLegOfIncludedFlight?.to?.landingDate; // landingDate is a string yyyy-MM-dd
			hasArrivalDayShift = landingDateIncludedFlight !== endDateSearch;
		}

		track("quotationPage_result", {
			sortBy: sortedBy,
			daysBeforeDeparture: differenceInDays(new Date(booking.departureDate), new Date()),
			quote: {
				httpStatusCode: String(status),
				status: data.status,
				hasArrivalDayShift,
				connectivitySessionId: data.code,
				flightCount: data.flights ? String(data.flights.length) : "",
				accommodationCount: data.accommodations?.accommodationItems
					? String(data.accommodations.accommodationItems.length)
					: "",
				boardCount: data.accommodations?.boards
					? String(data.accommodations.boards.length)
					: "",
				transferCount: data.transfers ? String(data.transfers.length) : "",
				activityCount: data.activities ? String(data.activities.length) : "",
				quotePrice:
					data.quotationItems?.[0] && data.quotationItems?.[1]
						? String(data.quotationItems[0].price + data.quotationItems[1].price)
						: "",
			},
		});
	};

	const runSDPAvailabilities = ({ code, query, price }) => {
		const { accommodationId, boardCode } = query;

		checkSDPAvailibilities({
			code,
			accommodationId,
			boardCode,
			price: Number(price),
			abTests: ppFunnelFlagsValues,
		}).then(response => {
			// une quote annulée (hôtel quitté) ne doit ni être taguée ni déclencher la suite
			if (isRequestAborted(response)) {
				return;
			}

			trackQuotationResult(response);

			const flights = response.data?.flights || [];

			if (flights?.length > 0) {
				fetchFlightOptions({
					flight: flights[0].code,
					code: response?.data.code,
					accommodationId,
				}).then(response => {
					if (hasFlightOptionsFailed(response)) {
						track("flightOptions_fail");
					}
				});
			}
		});
	};

	const handleSDPDirectAccessQuote = query => {
		const searchQuery = extractSDPSearchQueryFromQuoteQuery(query);
		const bookingFromQuery = mapQueryParamsToBooking({
			destinations: sdpDestinations,
			departureCities: sdpDepartureCities,
			cabins: sdpCabins,
			queryParams: query,
		});

		isSDPDirectAccessRef.current = true;
		updateBooking(bookingFromQuery);

		fetchSdpQuote(
			buildSDPQuotePayload({
				queryParams: query,
				currency,
				partnerCode,
				destinations: sdpDestinations,
				sdpFunnelFlags: ppFunnelFlagsValues,
				duration: Number(query.duration),
			})
		).then(response => {
			if (isRequestAborted(response)) {
				return;
			}

			const httpStatusCode = response?.status;
			const data = response?.data || {};

			if (httpStatusCode === HTTP_STATUS_CODES.FORBIDDEN) {
				const onauthsuccess = encodeURIComponent(`${location.pathname}${location.search}`);

				navigate(
					{
						pathname: "/booking-auth",
						search: `?onauthsuccess=${onauthsuccess}`,
					},
					{ replace: true }
				);
				return;
			}

			if (httpStatusCode !== HTTP_STATUS_CODES.OK) {
				navigate(
					{ pathname: "/technical-error" },
					{ replace: true, state: { errorMessage: data.message } }
				);
				return;
			}

			saveSDPSearchQueryString(stringify(searchQuery));

			const selectedAccommodation = data.accommodationsList?.find(
				accommodation => String(accommodation.id) === String(query.accommodationId)
			);

			if (!selectedAccommodation) {
				navigate(
					{ pathname: "/sdp/listing", search: stringify(searchQuery) },
					{ replace: true }
				);
				return;
			}

			saveSDPProduct(selectedAccommodation);
			runSDPAvailabilities({
				code: data.code,
				query,
				price: query.price ?? selectedAccommodation.total,
			});
		});
	};

	const handleSDPQuote = query => {
		// do not fetch availibilities again if already fetched (quotation.code is not undefined)
		// quotation.code is reset when we clicked on a sdp product (look for clearQuotation() usage)
		// fetch availabilities will reset accommodations and boards codes => user selection can not be persisted
		if (quotation.code) {
			return;
		}

		// SDP gère les bébés via les occupancies, pas via booking.infants. On remet à 0
		// pour éviter qu'une valeur périmée (persistée depuis une session précédente en prepack
		// avec bébés) n'affiche à tort le message d'avertissement bagages bébé.
		updateBooking({ infants: 0 });

		const codeFromNavigation = location.state?.code;
		const isPersistedCodeReusable = isSDPQuoteCodeReusable({
			code: smartDPCode,
			quoteQuery: query,
			searchQueryString,
			quoteTimestamp: sdpQuoteTimestamp,
		});

		if (codeFromNavigation || isPersistedCodeReusable) {
			runSDPAvailabilities({
				code: codeFromNavigation || smartDPCode,
				query,
				price: query.price || 0,
			});
		} else if (hasSDPQuoteSearchParams(query)) {
			handleSDPDirectAccessQuote(query);
		} else {
			redirectToFallbackUrl();
		}
	};

	const handleStopoverQuote = query => {
		const { accommodationId, boardCode, price = 0, stopoverDuration } = query;

		// do not fetch availibilities again if already fetched (quotation.code is not undefined)
		// quotation.code is reset when we clicked on a sdp product (look for clearQuotation() usage)
		// fetch availabilities will reset accommodations and boards codes => user selection can not be persisted
		if (!quotation.code) {
			// Stopover gère les bébés via les occupancies, pas via booking.infants. On remet à 0
			// pour éviter qu'une valeur périmée (persistée depuis une session précédente en prepack
			// avec bébés) n'affiche à tort le message d'avertissement bagages bébé.
			updateBooking({ infants: 0 });
			checkStopoverAvailibilities({
				bookingFlowId: location.state?.code || smartDPCode,
				accommodationId,
				boardCode,
				price: Number(price),
				abTests: ppFunnelFlagsValues,
				stopoverDuration: stopoverDuration,
			}).then(response => {
				// une quote annulée (hôtel quitté) ne doit ni être taguée ni déclencher la suite
				if (isRequestAborted(response)) {
					return;
				}

				trackQuotationResult(response);

				// get flight code from store as is it already selected in the stopover listing page
				fetchStopoverFlightOptions({
					flight: selectedflightCode,
					bookingFlowId: response?.data?.bookingFlowId,
					accommodationId,
					stopOverDuration: Number(stopoverDuration),
				}).then(response => {
					if (hasFlightOptionsFailed(response)) {
						track("flightOptions_fail");
					}
				});
			});
		}
	};

	const redirectToFallbackUrl = () => {
		if (fallbackRedirectUrl.indexOf("https") !== -1) {
			window.location = fallbackRedirectUrl;
		} else {
			// hack to fix "You should call navigate() in a React.useEffect(), not when your component is first rendered"
			// may be deleted when moving this component to functional
			setTimeout(() => {
				navigate({ pathname: fallbackRedirectUrl });
			}, 100);
		}
	};

	const handlePrepackQuote = query => {
		// Ici on est dans le cas d'une quotation PP (/booking/quote)

		const quotationFromQuery = extractQuotationPayloadFromQueryParams(query);

		if (!quotationFromQuery || isEmpty(quotationFromQuery)) {
			// si on arrive ici, c'est qu'on a chargé la page quotation directement sans passer par le tunnel de resa
			redirectToFallbackUrl();
		}

		const productUri = query.productUri || ficheProduit.uri;

		/**
		 * we need to call fetchFicheProduit before checkAvailibilities because checkAvailibilities needs config in payload that depends
		 * on the product json and offerType in qoutation url.
		 */
		fetchFicheProduit({ productUri }).then(res => {
			// do not fetch availibilities again if already fetched (quotation.code is not undefined)
			// quotation.code is reset when we clicked on a product (look for clearQuotation() usage)
			// fetch availabilities will reset accommodations and boards codes => user selection can not be persisted

			if (res?.status !== HTTP_STATUS_CODES.OK) {
				track("fetch_product_json_fail", {
					productUri: productUri,
					httpStatus: res?.status,
				});

				navigate(
					{
						pathname: "/technical-error",
					},
					{
						replace: true,
					}
				); // replace pour éviter un back navigateur sur la page de quotation
			} else if (res?.data?.endAt && res?.data?.endAt - new Date().getTime() <= 0) {
				navigate({ pathname: `/${productUri}` }, { replace: true });
			} else if (!quotation.code || quotation.searchQueryString !== location.search) {
				/**
				 * the config, mandatory in the quotation payload, is no more fetched from the quotation url but
				 * is fetch from the product json using productUri and the offerType from quotation url.
				 */
				const selectedOfferData = res.data?.offers?.find(
					offer => offer?.pricing?.type === quotationFromQuery.offerType
				);

				const { departureCities = [], alternativeDepartureCities = [] } =
					selectedOfferData?.pricing || {};

				let departureCityLabel;
				if (quotationFromQuery.offerType === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION) {
					departureCityLabel = [...departureCities, ...alternativeDepartureCities].find(
						city => city.code === quotationFromQuery.departureCityCode
					)?.label;
				}

				const productId = res?.data?.id;

				if (!isEmpty(quotationFromQuery)) {
					updateBooking(
						buildBookingUpdatedData({
							...quotationFromQuery,
							config: selectedOfferData?.pricing?.config,
							offerLabel: selectedOfferData?.pricing?.label,
							contractType: selectedOfferData?.pricing?.contractType,
							negotiated: selectedOfferData?.negotiated,
							departureCityLabel,
						})
					);
				}

				checkAvailibilities(
					buildQuotationPayload({
						quotationPayload: {
							...quotationFromQuery,
							config: selectedOfferData?.pricing?.config,
							productId,
							partnerCode,
							abTests: ppFunnelFlagsValues,
						},
						sessionBiid,
					})
				).then(response => {
					// une quote annulée (produit quitté) ne doit rien écrire dans le store, ni taguer,
					// ni rediriger : son status est undefined et déclencherait la /technical-error
					if (isRequestAborted(response)) {
						return;
					}

					updateExchangeRate({
						usd: response?.data?.exchangeRateUSD,
					});
					saveQuotationSearchQueryString(location.search);

					const quoteURL = `${window.location.protocol}//${
						window.location.host
					}/${shop}/booking/quote`;

					const quotationReloadURL = buildQuotationReloadUrl(quoteURL, {
						booking: {
							...booking,
							...buildBookingUpdatedData(quotationFromQuery),
						},
						ficheProduit: res.data, // take the response of fetchFicheProduit instead of the store. It will be up to date
					});

					track("quotation_page", {
						referrer: document.referrer || "",
						quote: { quotationReloadUrl: quotationReloadURL },
					});
					trackQuotationResult(response);

					const isQuotationFull = [
						QUOTATION_CODE_STATUS.MISSING_INCLUDED_ACTIVITY,
						QUOTATION_CODE_STATUS.FULL_FLIGHT,
						QUOTATION_CODE_STATUS.FULL_PROPERTY,
						QUOTATION_CODE_STATUS.FULL_TRANSFER,
					].includes(get(response, "data.status"));

					const quoteHttpStatus = response.status;
					const httpErrorMessage = get(response, "data.message");

					if (
						!quoteHttpStatus ||
						quoteHttpStatus === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR ||
						quoteHttpStatus === HTTP_STATUS_CODES.GATEWAY_TIMEOUT ||
						quoteHttpStatus === HTTP_STATUS_CODES.BAD_REQUEST
					) {
						track("quotationPage_fail", { httpStatus: String(quoteHttpStatus) });
						navigate(
							{
								pathname: "/technical-error",
							},
							{
								replace: true,
								state: {
									errorMessage: httpErrorMessage,
								},
							}
						); // replace pour éviter un back navigateur sur la page de quotation
					} else if (quoteHttpStatus === HTTP_STATUS_CODES.FORBIDDEN) {
						track("quotationPage_fail", { httpStatus: String(quoteHttpStatus) });

						if (strategy === STRATEGY.OAUTH) {
							navigate({ pathname: "/technical-error/sso" });
						} else if (strategy === STRATEGY.TRANSACTION_FIRST) {
							if (token) {
								// avant logoutUser : LOGOUT vide les slices auth, profile,
								// coupons et sponsorship dont le tronc commun tire user
								track("logout");
								logoutUser(shop);
								window.location = `${window.location.protocol}//${
									window.location.host
								}${window.location.pathname}${window.location.search}`;
							} else {
								navigate(
									{
										pathname: "/technical-error",
									},
									{
										replace: true,
										state: {
											errorMessage: httpErrorMessage,
										},
									}
								);
							}
						} else {
							const onauthsuccess = encodeURIComponent(
								`${location.pathname}${location.search}`
							);

							navigate(
								{
									pathname: "/booking/auth",
									search: `?onauthsuccess=${onauthsuccess}`,
								},
								{ replace: true }
							); // replace pour éviter un back navigateur sur la page de quotation
						}
					} else if (quoteHttpStatus === HTTP_STATUS_CODES.OK && isQuotationFull) {
						navigate("/booking/search", {
							replace: true,
							state: {
								alertMessageId: "error.no.availabilities.search",
							},
						});
					} else if (
						quoteHttpStatus === HTTP_STATUS_CODES.OK &&
						QUOTATION_STATUS_VALIDE.includes(get(response, "data.status"))
					) {
						// TOUT EST OK
						if (isNavlogsEnabled && !hasEmptyParams(quotationReloadURL)) {
							// dans le cas où on arrive d'une quotationURL
							// et qu'on fetch le produit sur une page /quote
							saveNavLog({
								product: {
									id: res?.data?.id,
								},
								quotationURL: quotationReloadURL,
							});
						}

						const flights = response.data?.flights || [];

						if (flights?.length > 0) {
							fetchFlightOptions({
								flight: flights[0].code,
								code: response?.data.code,
							}).then(response => {
								if (hasFlightOptionsFailed(response)) {
									track("flightOptions_fail");
								}
							});
						}
					} else {
						// here we got a 200 but with a code other than SUCCESS

						track("quotationPage_fail", { httpStatus: String(quoteHttpStatus) });
						navigate(
							{
								pathname: "/technical-error",
							},
							{
								replace: true,
								state: {
									errorMessage: httpErrorMessage,
								},
							}
						); // replace pour éviter un back navigateur sur la page de quotation
					}
				});
			}
		});
	};

	useEffect(() => {
		clearPromotionValue({ promoCode: "", code: quotation.code });

		if (isBookingEligibleForDeposit) {
			track("Quote_deposit_available");
		}

		return () => {
			finishLoadingBar();
			showFlexRatesModal(false);
		};
	}, [location.search, brand]);

	useEffect(() => {
		// Flagship refetch à chaque changement du contexte visiteur (deltaBasePrice, offerType…) :
		// une seule quote par recherche, sinon chaque nouvelle quote annule celle encore en vol.
		// Seule la quote prepack envoie le sessionBiid, créé ou renouvelé (si expiré) par l'effet
		// de PerfectAppContainer qui s'exécute après celui-ci.
		const isPrepackQuote = !isSDPQuote && !isStopoverQuote;
		const isSessionBiidMissing =
			!sessionBiid ||
			isExpiredAfter(sessionBiidExpirationDate, SESSION_BIID_EXPIRATION_MAX_AGE);

		if (
			!isFlagshipDecisionSettled ||
			(isPrepackQuote && isSessionBiidMissing) ||
			quotedSearchRef.current === location.search
		) {
			return;
		}

		quotedSearchRef.current = location.search;

		const query = parseQueries(location.search);

		if (isSDPQuote) {
			handleSDPQuote(query);
		} else if (isStopoverQuote) {
			handleStopoverQuote(query);
		} else {
			handlePrepackQuote(query);
		}
	}, [location.search, isFlagshipDecisionSettled, sessionBiid]);

	useEffect(() => {
		// @see https://perfectstay.atlassian.net/browse/FBR-4911
		// Annuler la quote en vol au démontage, sinon sa réponse tardive écrit l'hôtel quitté dans
		// les slices persistées. Le ref est vidé pour que le remontage StrictMode relance la quote.
		return () => {
			quotedSearchRef.current = undefined;

			if (isSDPQuote) {
				abortSdpQuote();
				abortSDPAvailibilities();
			} else if (isStopoverQuote) {
				abortStopoverAvailibilities();
			} else {
				abortAvailibilities();
			}
		};
	}, []);

	useEffect(() => {
		// les libellés du booking (ville de départ, destination, cabine) dépendent des filtres sdp,
		// chargés en parallèle de la quote rejouée depuis l'url
		if (isSDPDirectAccessRef.current && sdpDestinations?.length > 0) {
			updateBooking(
				mapQueryParamsToBooking({
					destinations: sdpDestinations,
					departureCities: sdpDepartureCities,
					cabins: sdpCabins,
					queryParams: parseQueries(location.search),
				})
			);
		}
	}, [sdpDestinations, sdpDepartureCities, sdpCabins]);

	const handleSubmit = async values => {
		track("proceed_payment");
		showLoadingBar();

		const payload = mapFormToPayload(values, { abTestsValues, location });
		const response = await preBook(payload);

		if (response?.status === HTTP_STATUS_CODES.OK) {
			handleSuccesPreBookResponse(response, navigate, false, location, track);
		} else {
			handleFailPreBookResponse(response, navigate, strategy, location);
			track("prebook_fail");
		}

		finishLoadingBar();
	};

	const handleValidation = () => {
		const errors = {};

		const duplicateDates = computeActivitiesDuplicateDates(booking?.activities);

		if (duplicateDates?.length > 0) {
			errors.duplicateDates = duplicateDates;
		}

		return errors;
	};

	return (
		<>
			{titleFromSanity && descriptionFromSanity && (
				<HeadMetadata title={titleFromSanity} description={descriptionFromSanity} />
			)}

			<Formik
				initialValues={formInitialValues}
				onSubmit={handleSubmit}
				enableReinitialize
				validateOnChange={false}
				validateOnBlur={false}
				validate={handleValidation}
			>
				<Quotation
					{...props}
					{...stateProps}
					{...dispatchProps}
					isStopover={isStopoverQuote}
				/>
			</Formik>
		</>
	);
};

QuotationContainer.propTypes = {
	checkAvailibilities: PropTypes.func,
	checkSDPAvailibilities: PropTypes.func,
	checkStopoverAvailibilities: PropTypes.func,
	clearPromotionValue: PropTypes.func,
	fetchFicheProduit: PropTypes.func,
	fetchFlightOptions: PropTypes.func,
	fetchStopoverFlightOptions: PropTypes.func,
	logoutUser: PropTypes.func,
	navigate: PropTypes.func,
	preBook: PropTypes.func,
	saveQuotationSearchQueryString: PropTypes.func,
	showAlert: PropTypes.func,
	showFlexRatesModal: PropTypes.func,
	updateBooking: PropTypes.func,

	location: PropTypes.shape({
		pathname: PropTypes.string,
		search: PropTypes.string,
		state: PropTypes.object,
	}),

	booking: bookingPropTypes,
	quotation: quotationPropTypes,

	abTestsValues: PropTypes.object,
	fallbackRedirectUrl: PropTypes.string,
	ficheProduit: PropTypes.object,
	formInitialValues: PropTypes.object,
	isBookingEligibleForDeposit: PropTypes.bool,
	isNavlogsEnabled: PropTypes.bool,
	partnerCode: PropTypes.string,
	selectedflightCode: PropTypes.string,
	sessionBiid: PropTypes.string,
	sessionBiidExpirationDate: PropTypes.number,
	smartDPCode: PropTypes.string,
	sortedBy: PropTypes.string,
	strategy: PropTypes.string,
	token: PropTypes.string,
	updateExchangeRate: PropTypes.func,
	titleFromSanity: PropTypes.string,
	descriptionFromSanity: PropTypes.string,
	currency: PropTypes.string,
	sdpDestinations: PropTypes.array,
	sdpDepartureCities: PropTypes.array,
	sdpCabins: PropTypes.array,
	searchQueryString: PropTypes.string,
	sdpQuoteTimestamp: PropTypes.number,
	fetchSdpQuote: PropTypes.func,
	saveSDPProduct: PropTypes.func,
	saveSDPSearchQueryString: PropTypes.func,
};

// getBaggagePayload returns the payload for the luggage or cabin luggage from the form values, even though they might be the same we don't want to pass unnecessary fields.
const getBaggagePayload = formBaggage => {
	if (!formBaggage.quantity) {
		return null;
	}

	const payload = {
		quantity: formBaggage.quantity,
	};

	if (formBaggage.code) {
		payload.code = formBaggage.code;
	}

	if (formBaggage.maxWeightPerBaggage) {
		payload.maxWeightPerBaggage = formBaggage.maxWeightPerBaggage;
	}

	return payload;
};

const mapFormToPayload = (values, ownProps) => {
	const state = getStore().getState();

	const booking = state.booking;
	let newPayload = {
		code: state.quotation.code,
		accommodationBoardCode: values.board,
		flight: values.flight,
		transfer: values.transfer,
		totalPrice: calculatePreBookTotal(state),
		abTests: ownProps.abTestsValues,
	};

	const isStopover = ownProps.location?.pathname?.includes("/stopover/booking/quote");
	if (isStopover) {
		newPayload.bookingFlowId = state.quotation.code;
		delete newPayload.code;
		newPayload.stopOverDuration = state.booking?.stopoverDuration;
	}

	newPayload.flightOptions = {
		flightBagageOption: {},
	};

	const flightOptionsOfSelectedFlight = getFlightOptionsOfSelectedFlight(state) || {};
	if (
		flightOptionsOfSelectedFlight.flightBagageOption &&
		!flightOptionsOfSelectedFlight.flightBagageOption?.included &&
		values.luggage?.quantity > 0
	) {
		newPayload.flightOptions.flightBagageOption.holdBaggage = getBaggagePayload(values.luggage);
	}

	if (values.cabinLuggage.quantity > 0) {
		newPayload.flightOptions.flightBagageOption.cabinBaggage = getBaggagePayload(
			values.cabinLuggage
		);
	}

	if (booking.activities.length > 0) {
		newPayload.activities = booking.activities.filter(activity => {
			activity.guests = activity.guests.filter(guest => {
				return guest.guestsCount > 0;
			});

			return activity.guests.length > 0 ? activity : false;
		});
	}

	if (booking.insuranceFlex) {
		const { code } = booking.insuranceFlex;
		newPayload.flexInsuranceCode = code !== INSURANCE_STANDARD ? code : null;
	}

	return newPayload;
};

const mapStateToProps = state => {
	const includedFlight = getIncludedFlight(state);
	const flights = getVisibleFlights(state);
	const booking = state.booking;
	const activities = state.quotation.activities;

	const formInitialValues = {
		accommodation: !isEmpty(booking.accommodation) ? booking.accommodation.code : undefined,
		flight: !isEmpty(booking.flight) ? booking.flight.code : undefined,
		board: !isEmpty(booking.board) ? booking.board.code : undefined,
		transfer: !isEmpty(booking.transfer) ? booking.transfer.code : undefined,
		activities: activities.map(activity => {
			const activityDate =
				booking?.activities?.find(act => act?.code === activity?.code)?.date ||
				activity?.suggestedDate;

			return {
				code: activity?.code,
				date: activityDate,
				guests: activity?.guestsPerDay?.[activityDate]?.reduce((acc, guest) => {
					const nbOfGuests =
						booking?.activities
							?.find(act => act?.code === activity?.code)
							?.guests?.find(g => g.code === guest.code)?.guestsCount ||
						guest.includedGuest;

					acc[guest.code] = nbOfGuests;
					return acc;
				}, {}),
			};
		}),
		luggage: booking.luggage,
		cabinLuggage: booking.cabinLuggage,
	};

	const enableFlexRateChoice = shouldEnableFlexRateChoice(state);

	const { token } = getCredentials(state.shop);

	return {
		transfers: getTransfersOfSelectedFlight(state),
		booking,
		selectedflightCode: booking.flight?.code,
		strategy: state.partner.strategy,
		quotation: state.quotation,
		isBookingEligibleForDeposit: isBookingEligibleForDeposit(state),
		flights: flights,
		includedFlight,
		partnerCode: state.partner.code,
		sessionBiid: getSessionBiid(state),
		sessionBiidExpirationDate: getSessionBiidExpirationDate(state),
		formInitialValues,
		ficheProduit: state.ficheProduit,
		resolution: state.resolution,
		searchQueryString: state.smartDP.searchQueryString,
		sortedBy: state.smartDP.sortedBy,
		isNavlogsEnabled: checkIfNavlogEnabled(state),
		hasSpecialOfferOnQuote: checkSpecialOfferOnQuote(state),
		specialOffer: getSpecialOffer(state),
		enableFlexRateChoice,
		smartDPCode: state.smartDP.code,
		prePackageOffers: state.prePackageOffer.offers,
		fallbackRedirectUrl: getFallbackRedirectUrl(state),
		isOptionFlexAvailable: hasOptionFlex(state),
		token: token,
		ficheProduitStatus: state.ficheProduit.status,
		priceChangeData: getQuotationPriceChangeData(state),
		flightLuggageOption: getFlightOptionsOfSelectedFlight(state)?.flightBagageOption,
		titleFromSanity: getSeoMetaData("bookingQuoteSDP.metaTitle")(state),
		descriptionFromSanity: getSeoMetaData("bookingQuoteSDP.metaDescription")(state),
		currency: state.currency,
		sdpDestinations: state.smartDP.destinations,
		sdpDepartureCities: state.smartDP.departureCities,
		sdpCabins: state.smartDP.cabins,
		sdpQuoteTimestamp: state.smartDP.timestamp,
	};
};

const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{
			checkStopoverAvailibilities,
			checkSDPAvailibilities,
			checkAvailibilities,
			updateBooking,
			clearPromotionValue,
			saveNavLog,
			fetchFicheProduit,
			showFlexRatesModal,
			logoutUser,
			saveQuotationSearchQueryString,
			clearSmartDP,
			fetchFlightOptions,
			fetchStopoverFlightOptions,
			preBook,
			updateExchangeRate,
			fetchSdpQuote,
			saveSDPProduct,
			saveSDPSearchQueryString,
		},
		dispatch
	);

export default withRouter(withFlagship(withAlert(QuotationContainer)));
